import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '@/lib/axios';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { userPreferencesService, UserPreferences } from '@/lib/services/userPreferences';

interface SubscriptionFeatures {
  // The 7 subscription sections
  fohTasks: boolean;
  setups: boolean;
  kitchen: boolean;
  documentation: boolean;
  training: boolean;
  evaluations: boolean;
  leadership: boolean;
  // Legacy feature - keeping for backward compatibility
  leadershipPlans: boolean;
  [key: string]: boolean;
}

interface SubscriptionPricing {
  sectionPrice: number;
  maxPrice: number;
}

interface SubscriptionTrialInfo {
  isInTrial: boolean;
  trialStartDate: string;
  trialEndDate: string;
}

interface SubscriptionPeriod {
  startDate: string;
  endDate: string;
}

interface PendingChanges {
  hasChanges: boolean;
  features: SubscriptionFeatures;
  effectiveDate: string;
  submittedAt: string;
}

interface SubscriptionState {
  hasActiveSubscription: boolean;
  subscriptionStatus: 'active' | 'expired' | 'trial' | 'none' | 'past_due' | 'canceled';
  features: SubscriptionFeatures;
  pricing: SubscriptionPricing;
  trialInfo: SubscriptionTrialInfo | null;
  currentPeriod: SubscriptionPeriod | null;
  pendingChanges: PendingChanges | null;
  calculatedCost: number;
  pendingCost: number | null;
  loading: boolean;
  error: string | null;
  userPreferences: UserPreferences | null;
  billingMethod?: 'stripe' | 'check';
}

interface SubscriptionContextType extends SubscriptionState {
  refreshSubscription: () => Promise<void>;
  toggleFeature: (feature: keyof SubscriptionFeatures, enabled: boolean) => Promise<void>;
  submitChanges: (featuresOverride?: SubscriptionFeatures) => Promise<void>;
  startTrial: () => Promise<void>;
  isFeatureEnabled: (feature: keyof SubscriptionFeatures) => boolean;
  hasPendingChanges: () => boolean;
  updateUserFeatureAccess: (featureAccess: any) => void;
  isDashboardFeatureVisible: (feature: keyof SubscriptionFeatures, cardType?: 'quickStats' | 'detailCards' | 'listCards') => boolean;
  updateDashboardPreferences: (dashboardPreferences: any) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [state, setState] = useState<SubscriptionState>({
    hasActiveSubscription: true, // Default to active
    subscriptionStatus: 'active', // Default to active
    features: {
      fohTasks: true, // All features enabled by default
      setups: true,
      kitchen: true,
      documentation: true,
      training: true,
      evaluations: true,
      leadership: true,
      leadershipPlans: true
    },
    pricing: {
      sectionPrice: 50,
      maxPrice: 200
    },
    trialInfo: null,
    currentPeriod: null,
    pendingChanges: null,
    calculatedCost: 200, // Default to max cost since all features are enabled
    pendingCost: null,
    loading: true,
    error: null,
    userPreferences: null,
    billingMethod: 'check'
  });

  const fetchSubscriptionStatus = async (forceRefresh = false) => {
    // Only check for user, since isAuthenticated might not be set yet
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Add a cache-busting parameter when force refreshing
      const url = forceRefresh
        ? `/api/subscriptions/status?t=${Date.now()}`
        : '/api/subscriptions/status';

      // Use the configured API client
      const response = await api.get(url);
      const data = response.data;

      // Determine if subscription is active based on status
      const hasActiveSubscription =
        data.subscriptionStatus === 'active' ||
        data.subscriptionStatus === 'trial';

      // Fetch user preferences
      let userPreferences: UserPreferences | null = null;
      try {
        userPreferences = await userPreferencesService.getUserPreferences();
      } catch (prefError) {
      }

      // Use the actual data from the server
      setState({
        ...data,
        hasActiveSubscription,
        loading: false,
        error: null,
        userPreferences
      });

      // Notify other contexts (e.g., AuthContext) that subscription status changed
      try {
        window.dispatchEvent(new CustomEvent('subscription:status-updated', {
          detail: { subscriptionStatus: data.subscriptionStatus, hasActiveSubscription }
        }));
      } catch {}

      return data;
    } catch (error) {

      // On error, set a default state with all features enabled
      setState({
        hasActiveSubscription: true,
        subscriptionStatus: 'active',
        features: {
          fohTasks: true,
          setups: true,
          kitchen: true,
          documentation: true,
          training: true,
          evaluations: true,
          leadership: true,
          leadershipPlans: true
        },
        pricing: {
          sectionPrice: 50,
          maxPrice: 200
        },
        trialInfo: null,
        currentPeriod: null,
        pendingChanges: null,
        calculatedCost: 200,
        pendingCost: null,
        loading: false,
        error: null, // Don't show error to prevent UI disruption
        userPreferences: null,
        billingMethod: 'check'
      });

      return null;
    }
  };

  // Toggle a subscription feature
  const toggleFeature = async (feature: keyof SubscriptionFeatures, enabled: boolean) => {
    try {
      setState(prev => ({ ...prev, loading: true }));

      const response = await api.put('/api/subscriptions/features', {
        features: {
          [feature]: enabled
        }
      });

      const data = response.data;

      // Determine if subscription is active based on status
      const hasActiveSubscription =
        data.subscriptionStatus === 'active' ||
        data.subscriptionStatus === 'trial';

      // Note: If subscription is expired, features will still be visible but disabled via isFeatureEnabled

      setState({
        ...data,
        hasActiveSubscription,
        loading: false,
        error: null
      });

      showNotification(
        'success',
        'Subscription Updated',
        `${feature} has been ${enabled ? 'enabled' : 'disabled'}.`
      );

      return data;
    } catch (error) {

      const axiosError = error as { response?: { status?: number; data?: { message?: string; code?: string } } };
      const errorData = axiosError?.response?.data;

      // Kept for safety: the server no longer refuses paid enables (store admins apply them
      // immediately and ops is emailed), but if the guard ever returns, roll the toggle back
      // and surface why so the change can go through submitChanges().
      if (axiosError?.response?.status === 403 && errorData?.code === 'SUBSCRIPTION_CHANGE_REQUIRED') {
        const message = errorData.message || 'This change requires a subscription change.';

        setState(prev => ({
          ...prev,
          features: {
            ...prev.features,
            [feature]: !enabled
          },
          loading: false,
          error: message
        }));

        showNotification(
          'error',
          'Subscription Change Required',
          message
        );

        return null;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to update subscription feature'
      }));

      showNotification(
        'error',
        'Error',
        'Failed to update subscription feature.'
      );

      return null;
    }
  };

  // Start a free trial
  const startTrial = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));

      const response = await api.post('/api/subscriptions/start-trial');
      const data = response.data;

      // Determine if subscription is active based on status
      const hasActiveSubscription =
        data.subscriptionStatus === 'active' ||
        data.subscriptionStatus === 'trial';

      // Note: If subscription is expired, features will still be visible but disabled via isFeatureEnabled

      setState({
        ...data,
        hasActiveSubscription,
        loading: false,
        error: null
      });

      showNotification(
        'success',
        'Trial Started',
        'Your 14-day free trial has started. Enjoy full access to all features!'
      );

      return data;
    } catch (error) {

      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to start trial'
      }));

      showNotification(
        'error',
        'Error',
        'Failed to start free trial.'
      );

      return null;
    }
  };

  // Submit subscription changes to take effect at next billing date
  const submitChanges = async (featuresOverride?: SubscriptionFeatures) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await api.post('/api/subscriptions/submit-changes', {
        features: featuresOverride || state.features
      });

      const data = response.data;

      // Determine if subscription is active based on status
      const hasActiveSubscription =
        data.subscriptionStatus === 'active' ||
        data.subscriptionStatus === 'trial';

      // Note: If subscription is expired, features will still be visible but disabled via isFeatureEnabled

      setState({
        ...data,
        hasActiveSubscription,
        loading: false,
        error: null
      });

      // Notify other contexts about status change
      try {
        window.dispatchEvent(new CustomEvent('subscription:status-updated', {
          detail: { subscriptionStatus: data.subscriptionStatus, hasActiveSubscription }
        }));
      } catch {}

      // Format the effective date for display
      const effectiveDate = data.pendingChanges?.effectiveDate
        ? new Date(data.pendingChanges.effectiveDate).toLocaleDateString()
        : 'next billing date';

      showNotification(
        'success',
        'Subscription Changes Submitted',
        `Your subscription changes will take effect on ${effectiveDate}.`
      );

      return data;
    } catch (error) {

      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to submit subscription changes'
      }));

      showNotification(
        'error',
        'Error',
        'Failed to submit subscription changes.'
      );

      return null;
    }
  };

  // Check if a feature is enabled (memoized to prevent unnecessary re-renders)
  const isFeatureEnabled = useCallback((feature: keyof SubscriptionFeatures): boolean => {
    // If subscription is expired or none, all features are disabled
    if (state.subscriptionStatus === 'expired' || state.subscriptionStatus === 'none') {
      return false;
    }

    // If in trial or active subscription, check if the feature is enabled at subscription level
    if (state.hasActiveSubscription && state.features[feature] === true) {
      // For FOH tasks, also check user preferences
      if (feature === 'fohTasks' && state.userPreferences?.featureAccess?.fohTasks === false) {
        return false;
      }

      // For Setup Sheets, also check user preferences
      if (feature === 'setups' && state.userPreferences?.featureAccess?.setups === false) {
        return false;
      }

      return true;
    }

    // If not active, feature is disabled
    return false;
  }, [state.subscriptionStatus, state.hasActiveSubscription, state.features, state.userPreferences]);

  // Check if there are pending changes
  const hasPendingChanges = (): boolean => {
    return state.pendingChanges?.hasChanges === true;
  };

  useEffect(() => {
    // Only fetch if user exists and has a store
    if (user?.store?._id) {
      // Force refresh on initial load
      fetchSubscriptionStatus(true);

      // Set up a refresh interval (every 10 minutes to reduce load)
      const intervalId = setInterval(() => {
        fetchSubscriptionStatus(false); // Don't force refresh on interval
      }, 600000); // 10 minutes

      return () => clearInterval(intervalId);
    }
  }, [user?.store?._id]); // Only depend on store ID to reduce re-renders

  const refreshSubscription = async () => {
    return await fetchSubscriptionStatus(true);
  };

  // Update user feature access immediately (for instant UI updates)
  const updateUserFeatureAccess = useCallback((featureAccess: any) => {
    setState(prev => ({
      ...prev,
      userPreferences: {
        ...(prev.userPreferences || {}),
        featureAccess: {
          ...prev.userPreferences?.featureAccess,
          ...featureAccess
        }
      } as UserPreferences
    }));
  }, []);

  // Update dashboard preferences immediately (for instant UI updates)
  const updateDashboardPreferences = useCallback((dashboardPreferences: any) => {
    setState(prev => ({
      ...prev,
      userPreferences: {
        ...(prev.userPreferences || {}),
        dashboardPreferences: {
          ...prev.userPreferences?.dashboardPreferences,
          ...dashboardPreferences
        }
      } as UserPreferences
    }));
  }, []);

  // Check if a dashboard feature should be visible (separate from navigation access)
  const isDashboardFeatureVisible = useCallback((feature: keyof SubscriptionFeatures, cardType: 'quickStats' | 'detailCards' | 'listCards' = 'quickStats'): boolean => {
    // First check if the feature is enabled at subscription level
    if (!isFeatureEnabled(feature)) {
      return false;
    }

    // Then check dashboard preferences
    const dashboardPrefs = state.userPreferences?.dashboardPreferences;
    if (!dashboardPrefs) {
      return true; // Default to visible if no preferences set
    }

    // Check the specific card type preference
    const cardTypePrefs = dashboardPrefs[cardType];
    if (!cardTypePrefs) {
      return true; // Default to visible if no preferences for this card type
    }

    // Return the user's preference for this feature in this card type
    return cardTypePrefs[feature] !== false; // Default to true if not explicitly set to false
  }, [isFeatureEnabled, state.userPreferences]);

  const value = {
    ...state,
    refreshSubscription,
    toggleFeature,
    submitChanges,
    startTrial,
    isFeatureEnabled,
    hasPendingChanges,
    updateUserFeatureAccess,
    isDashboardFeatureVisible,
    updateDashboardPreferences
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
